import { Link } from '@tanstack/react-router'
import { CalendarClock, MonitorSmartphone, ShieldCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { Logo, LogoMark } from '@/components/logo'
import { ThemeToggle } from '@/features/shell/components/theme-toggle'

const HIGHLIGHTS = [
  { icon: MonitorSmartphone, title: 'Every screen, one console', text: 'Pair players in seconds and watch health across your whole fleet.' },
  { icon: CalendarClock, title: 'Scheduling that survives outages', text: 'Players run your schedule locally — even when the internet doesn’t.' },
  { icon: ShieldCheck, title: 'Enterprise-grade by default', text: 'Roles, audit logs and 2FA for every team from day one.' },
]

function BrandPanel() {
  return (
    <div className="relative hidden overflow-hidden bg-[oklch(0.16_0.05_278)] text-white lg:flex lg:flex-col">
      {/* Gradient mesh */}
      <div className="absolute -top-40 -left-40 size-[36rem] rounded-full bg-[oklch(0.55_0.25_290)] opacity-50 blur-[120px]" aria-hidden />
      <div className="absolute -right-32 bottom-[-10rem] size-[32rem] rounded-full bg-[oklch(0.7_0.15_210)] opacity-35 blur-[120px]" aria-hidden />
      <div
        className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)] bg-[size:40px_40px]"
        aria-hidden
      />

      <div className="relative flex flex-1 flex-col justify-between p-10 xl:p-14">
        <Link to="/" className="w-fit rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-white/60">
          <span className="inline-flex items-center gap-2.5">
            <LogoMark className="size-9" />
            <span className="text-lg font-semibold tracking-tight">Signetix</span>
          </span>
        </Link>

        <div className="max-w-lg">
          <h2 className="text-4xl leading-[1.1] font-semibold tracking-tight xl:text-[2.75rem]">
            Run every screen
            <br />
            <span className="bg-gradient-to-r from-[oklch(0.85_0.1_290)] to-[oklch(0.85_0.12_205)] bg-clip-text text-transparent">from one place.</span>
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/70">
            Digital signage for teams that can’t afford a blank screen. Content, schedules and devices — managed with confidence.
          </p>

          {/* Live fleet preview */}
          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-2xl backdrop-blur-md">
            <div className="flex items-center justify-between text-xs text-white/60">
              <span>Fleet health</span>
              <span className="flex items-center gap-1.5">
                <span className="relative flex size-2">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
                </span>
                Live
              </span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                ['Online', '248', 'bg-emerald-400'],
                ['Warnings', '3', 'bg-amber-400'],
                ['Offline', '1', 'bg-slate-400'],
              ].map(([label, value, dot]) => (
                <div key={label} className="rounded-xl bg-white/[0.06] px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[11px] text-white/60">
                    <span className={`size-1.5 rounded-full ${dot}`} />
                    {label}
                  </div>
                  <div className="mt-0.5 text-xl font-semibold tabular">{value}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex h-10 items-end gap-1" aria-hidden>
              {[40, 55, 48, 70, 62, 80, 76, 90, 84, 95, 88, 100, 92, 97, 94, 99].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-[oklch(0.6_0.22_290)] to-[oklch(0.78_0.13_210)] opacity-80" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </div>

        <ul className="grid gap-5 xl:grid-cols-3">
          {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
            <li key={title} className="space-y-1.5">
              <Icon className="size-5 text-[oklch(0.82_0.12_250)]" strokeWidth={1.75} />
              <p className="text-sm font-medium">{title}</p>
              <p className="text-[13px] leading-relaxed text-white/55">{text}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function AuthLayout({ title, description, children, footer }: { title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
      <BrandPanel />
      <div className="relative flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6">
          <Logo className="lg:invisible" />
          <ThemeToggle />
        </div>
        <div className="flex flex-1 items-center justify-center px-4 pb-16 sm:px-6">
          <div className="w-full max-w-[400px] animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
            <div className="mb-8 space-y-1.5">
              <h1 className="text-[26px] leading-tight font-semibold tracking-tight">{title}</h1>
              {description && <p className="text-sm text-muted-foreground">{description}</p>}
            </div>
            {children}
            {footer && <div className="mt-8 text-center text-sm text-muted-foreground">{footer}</div>}
          </div>
        </div>
      </div>
    </div>
  )
}
